import format from 'date-fns/format';
import { useState } from 'react';

import { Calendar } from './Calendar';
import { Icon } from './Icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { tv } from './utils/cn';

interface SingleDateSelectorProps {
  date?: Date;
  onDateChange: (date?: Date) => void;
  className?: string;
  variant?: 'default' | 'subtle';
  disabled?: boolean;
  'aria-label'?: string;
}

const triggerVariant = tv({
  base: 'flex min-w-0 cursor-pointer items-center justify-between gap-2 rounded-sm border bg-transparent p-2 text-text-secondary outline-hidden',
  variants: {
    variant: {
      default:
        'border-bg-quaternary hover:border-border-secondary focus:border-border-accent-quaternary',
      subtle:
        'border-transparent hover:bg-bg-quaternary focus:border-bg-secondary',
    },
    disabled: {
      true: 'pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});

export const SingleDateSelector: React.FC<SingleDateSelectorProps> = ({
  date,
  onDateChange,
  className,
  variant,
  disabled = false,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);

  const handleDateChange = (date?: Date) => {
    onDateChange(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={ariaLabel}
          disabled={disabled}
          className={triggerVariant({ variant, disabled, className })}
        >
          <Icon name="Calendar" />
          {date ? format(date, 'PP') : <span>Select a date</span>}
          <Icon name="ChevronDown" className="ml-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date}
          onSelect={handleDateChange}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
};
